import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { XCircle, ArrowLeft } from 'lucide-react';

export default function PaymentCancelArtist() {
  const navigate = useNavigate();

  const handleHome = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen flex items-center justify-center flex-col p-4 sm:p-8 bg-gradient-to-br from-red-50 to-orange-50">
      <div className="text-center max-w-md w-full">
        {/* Cancel Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 100, delay: 0.2 }}
          className="mb-6"
        >
          <XCircle size={80} className="text-red-600 mx-auto" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl sm:text-4xl font-bold text-slate-800 mb-3"
        >
          Subscription Cancelled
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-base sm:text-lg text-gray-700 mb-6"
        >
          Your artist subscription payment was cancelled. No charges have been made to your account.
        </motion.p>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 mb-6 shadow-lg border-l-4 border-orange-500"
        >
          <h3 className="font-semibold text-gray-900 mb-3">Why did this happen?</h3>
          <ul className="text-sm text-gray-600 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="text-gray-400">•</span>
              <span>You closed the payment window</span>
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">•</span>
              <span>Your payment method was declined</span>
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">•</span>
              <span>You changed your mind about subscribing</span>
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">•</span>
              <span>A connection issue occurred during checkout</span>
            </li>
          </ul>
        </motion.div>

        {/* What You Can Do */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-blue-50 rounded-lg p-4 mb-8 text-left"
        >
          <h3 className="font-semibold text-blue-900 mb-3">What you can do:</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex gap-2">
              <span className="text-blue-600">✓</span>
              <span>Try subscribing again</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600">✓</span>
              <span>Use a different payment method</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600">✓</span>
              <span>Contact your bank if payment was declined</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600">✓</span>
              <span>Learn more about our subscription plans</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600">✓</span>
              <span>Reach out to our support team</span>
            </li>
          </ul>
        </motion.div>

        {/* Benefits Reminder */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-8 text-left"
        >
          <h3 className="font-semibold text-gray-900 mb-3">Your artist benefits:</h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex gap-2">
              <span className="text-purple-600">♪</span>
              <span>Upload unlimited songs</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-600">♪</span>
              <span>Track sales with detailed analytics</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-600">♪</span>
              <span>Featured artist status</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-600">♪</span>
              <span>Monthly royalty reports</span>
            </li>
          </ul>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="space-y-3"
        >
          <button
            onClick={handleHome}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition shadow-lg text-sm sm:text-base flex items-center justify-center gap-2"
          >
            <ArrowLeft size={18} />
            Try Subscribing Again
          </button>
          <button
            onClick={handleHome}
            className="w-full px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition text-sm sm:text-base"
          >
            Back to Home
          </button>
          <button
            onClick={() => window.location.href = 'mailto:theasidorshome@yahoo.com'}
            className="w-full px-6 py-3 bg-orange-100 text-orange-700 rounded-lg font-semibold hover:bg-orange-200 transition text-sm sm:text-base"
          >
            Contact Support
          </button>
        </motion.div>

        {/* FAQ Link */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-xs text-gray-500"
        >
          Questions?{' '}
          <a href="mailto:theasidorshome@yahoo.com" className="text-purple-600 hover:underline font-medium">
            Contact our support team
          </a>
        </motion.p>
      </div>
    </div>
  );
}